import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, AlertTriangle, User, Mail, Phone, Calendar, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const DeleteUser = () => {
  const { token, getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      setUser(response.data.data);
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchUser();
  }, [isAdmin, navigate, fetchUser]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');

      await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      // Redirect to manage users page after successful deletion
      navigate('/admin/users', { 
        state: { 
          message: `User ${user.full_name || user.email} has been deleted successfully.` 
        } 
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
      setDeleting(false);
    }
  };

  const formatRoleDisplay = (role) => {
    if (!role) return 'N/A';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">User Not Found</h1>
            <button
              onClick={() => navigate('/admin/users')}
              className="text-yellow-500 hover:text-yellow-600"
            >
              Return to Manage Users
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => navigate('/admin/users')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Manage Users
          </button>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Warning Banner */}
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mr-4" />
              <div>
                <h2 className="text-xl font-bold text-red-800 mb-1">Delete User</h2>
                <p className="text-red-700">
                  You are about to permanently delete this user. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* User Details Card */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">User Information</h3>
            
            <div className="space-y-4">
              <div className="flex items-start border-b border-gray-200 pb-4">
                <User className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Full Name</p>
                  <p className="text-lg font-semibold text-gray-900">{user.full_name || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Mail className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Email</p>
                  <p className="text-lg font-semibold text-gray-900">{user.email}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Phone className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Phone</p>
                  <p className="text-lg font-semibold text-gray-900">{user.phone || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Shield className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Role</p>
                  <p className="text-lg font-semibold text-gray-900">{formatRoleDisplay(user.role)}</p>
                </div>
              </div>

              <div className="flex items-start border-b border-gray-200 pb-4">
                <Calendar className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Calendar Associations</p>
                  <p className="text-lg font-semibold text-gray-900">{user.calendar_names || 'None'}</p>
                </div>
              </div>

              {user.plan_type && (
                <div className="flex items-start pb-4">
                  <Shield className="w-6 h-6 text-gray-400 mr-4 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Subscription Plan</p>
                    <p className="text-lg font-semibold text-gray-900 capitalize">{user.plan_type}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Warning Info - Different content based on user role */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-yellow-800 mb-3">What will be deleted:</h4>
            
            {user.role === 'calendar_admin' ? (
              /* Calendar Admin - Full Cascade Delete */
              <div className="space-y-3">
                <p className="text-yellow-700 font-medium text-sm mb-2">
                  ⚠️ This is a full cascade delete. ALL resources owned by this admin will be permanently removed:
                </p>
                <ul className="list-disc list-inside space-y-2 text-yellow-700">
                  <li><strong>User account</strong> and profile information</li>
                  <li><strong>All calendars</strong> owned by this admin</li>
                  <li><strong>All services</strong> created by this admin</li>
                  <li><strong>All servers</strong> created by this admin</li>
                  <li><strong>All appointments</strong> on their calendars</li>
                  <li><strong>All availability slots</strong> on their calendars</li>
                  <li><strong>All customer registrations</strong> to their calendars</li>
                  <li><strong>All messages</strong> sent/received by this admin</li>
                  <li><strong>Calendar-service</strong> and <strong>calendar-server</strong> associations</li>
                </ul>
                <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md">
                  <p className="text-red-700 text-sm font-medium">
                    🚨 This action cannot be undone. All data associated with this Calendar Admin will be permanently lost.
                  </p>
                </div>
              </div>
            ) : (
              /* Customer / Non-Registered Customer */
              <ul className="list-disc list-inside space-y-2 text-yellow-700">
                <li>User account and profile information</li>
                <li>All calendar registrations</li>
                <li>All appointments (will be cancelled)</li>
                <li>All messages sent/received</li>
                <li>Activity history</li>
              </ul>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 px-6 rounded-md transition-colors"
            >
              Cancel - Keep User
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {deleting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Confirm Delete User
                </>
              )}
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DeleteUser;
